import NavDrawer from './NavDrawer';
import { useEffect, useState } from 'react';
import { supabase } from './supabaseClient';
import { SETS, PUBLISHED_SET_SUMMARIES } from './timelineData';
import SuggestEditModal from './SuggestEditModal';

const STATUS = {
  C: { label: 'CONFIRMED', color: 'var(--green)' },
  P: { label: 'PREDICTED', color: 'var(--gold)' },
  U: { label: 'UNKNOWN',   color: 'var(--muted-2)' },
};

// Sort key for a timeline date: 'Mar 31, 2023' → that day, 'Jul 2016' → the
// 1st of that month, anything else ('Unknown') → null.
const MONTHS = 'JanFebMarAprMayJunJulAugSepOctNovDec';
const dateSortKey = (date) => {
  const m = /^([A-Z][a-z]{2})(?: (\d{1,2}),)? (\d{4})$/.exec(date || '');
  const month = m ? MONTHS.indexOf(m[1]) / 3 : -1;
  if (!m || month < 0 || !Number.isInteger(month)) return null;
  return new Date(Number(m[3]), month, m[2] ? Number(m[2]) : 1).getTime();
};

// Newest release first; unknown dates last. Array.sort is stable, so ties
// (same date, or several unknowns) keep their order from timelineData.js.
const byReleaseDesc = (a, b) => {
  const ka = dateSortKey(a.date), kb = dateSortKey(b.date);
  if (ka === null || kb === null) return (ka === null) - (kb === null);
  return kb - ka;
};

export default function ResourcesTimeline() {
  const [user, setUser] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });
  }, []);

  // Group SETS by product year (the year in the set's name), then sort each
  // group by release date — some sets ship the following year.
  const yearGroups = SETS.reduce((acc, s) => {
    if (!acc[s.year]) acc[s.year] = [];
    acc[s.year].push(s);
    return acc;
  }, {});
  Object.values(yearGroups).forEach(group => group.sort(byReleaseDesc));
  // Newest year first
  const years = Object.keys(yearGroups).sort((a, b) => Number(b) - Number(a));

  const total = SETS.length;
  const confirmedCount = SETS.filter(s => s.status === 'C').length;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', fontFamily: "'Share Tech Mono', monospace" }}>
      <style>{`
        .rt-wrap {
          max-width: 1100px;
          margin: 0 auto;
          padding: 40px 24px 80px;
        }
        .rt-clear-nav {
          padding-right: 56px;
        }
        @media (max-width: 560px) {
          .rt-wrap { padding: 28px 16px 60px; }
          .rt-clear-nav { padding-right: 48px; }
        }
      `}</style>
      {user && <NavDrawer user={user} />}
      <div className="rt-wrap">
        {/* HEADER (right-padded so the nav hamburger doesn't crash into the title) */}
        <div className="rt-clear-nav" style={{ paddingBottom: '28px', borderBottom: '1px solid var(--border)', marginBottom: '28px' }}>
          <a href="/resources" style={{ fontFamily: "'Orbitron', monospace", fontSize: '9px', color: 'var(--muted-2)', letterSpacing: '3px', textDecoration: 'none', display: 'inline-block', marginBottom: '16px' }}>← BACK TO RESOURCES</a>
          <div style={{ fontFamily: "'Orbitron', monospace", fontSize: '9px', color: 'var(--brand-gold)', letterSpacing: '4px', marginBottom: '8px' }}>COLLECTION KEEPER · RESOURCES</div>
          <div style={{ fontFamily: "'Orbitron', monospace", fontSize: '28px', fontWeight: 900, color: 'var(--text-strong)', letterSpacing: '2px', marginBottom: '6px' }}>RELEASE TIMELINE</div>
          <div style={{ fontSize: '11px', color: 'var(--muted-2)', letterSpacing: '2px' }}>// EVERY TOPPS STAR WARS SET FROM 2015 TO 2026 //</div>
        </div>

        {/* STATUS LEGEND + SUGGEST BUTTON */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '12px', flexWrap: 'wrap' }}>
          {Object.entries(STATUS).map(([code, { label, color }]) => (
            <div key={code} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: color, flexShrink: 0 }} />
              <span style={{ fontSize: '10px', color, letterSpacing: '2px' }}>{label}</span>
            </div>
          ))}
          <div style={{ flex: 1 }} />
          <button
            type="button"
            onClick={() => setEditModalOpen(true)}
            style={{
              background: 'transparent',
              border: '1px solid rgba(var(--slate-rgb),0.45)',
              color: 'var(--muted-8)',
              fontFamily: "'Orbitron', monospace",
              fontSize: '9.5px',
              fontWeight: 600,
              letterSpacing: '2px',
              padding: '8px 14px',
              cursor: 'pointer',
              borderRadius: '2px',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--cyan)';
              e.currentTarget.style.color = 'var(--cyan)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'rgba(var(--slate-rgb),0.45)';
              e.currentTarget.style.color = 'var(--muted-8)';
            }}
          >
            ⚐ SUGGEST AN EDIT
          </button>
        </div>
        <div style={{ fontSize: '11px', color: 'var(--muted-2)', lineHeight: 1.6, marginBottom: '32px' }}>
          Sets are grouped by the year in their name. Some ship the following year.
        </div>

        {/* YEAR GROUPS */}
        {years.map((year) => (
          <div key={year} style={{ marginBottom: '36px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '14px' }}>
              <div style={{ fontFamily: "'Orbitron', monospace", fontSize: '14px', letterSpacing: '4px', color: 'var(--cyan)', whiteSpace: 'nowrap' }}>{year}</div>
              <div style={{ flex: 1, height: '1px', background: 'var(--border)' }} />
              <div style={{ fontFamily: "'Orbitron', monospace", fontSize: '9px', color: 'var(--border-2)', background: 'var(--panel-deep)', border: '1px solid var(--border)', padding: '2px 8px', borderRadius: '2px' }}>
                {yearGroups[year].length} {yearGroups[year].length === 1 ? 'SET' : 'SETS'}
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {yearGroups[year].map((s, i) => {
                const statusInfo = STATUS[s.status] || STATUS.U;
                const isLinked = s.setId && PUBLISHED_SET_SUMMARIES.includes(s.setId);

                const RowInner = (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: statusInfo.color, marginTop: '6px', flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '10px', marginBottom: '4px' }}>
                        <div style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: '11px', color: 'var(--muted)', letterSpacing: '1px', whiteSpace: 'nowrap' }}>{s.date}</div>
                        <div style={{ fontFamily: "'Orbitron', monospace", fontSize: '11px', letterSpacing: '1px', color: isLinked ? 'var(--gold)' : 'var(--text-strong)' }}>
                          {s.name}{isLinked ? ' ↗' : ''}
                        </div>
                      </div>
                      {s.notes && (
                        <div style={{ fontSize: '11px', color: '#5a7090', lineHeight: 1.5 }}>{s.notes}</div>
                      )}
                    </div>
                  </div>
                );

                const baseStyle = {
                  background: 'var(--panel-deep)',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  padding: '10px 14px',
                  transition: 'border-color 0.2s ease, background 0.2s ease',
                };

                if (isLinked) {
                  return (
                    <a
                      key={i}
                      href={'/resources/sets/' + s.setId}
                      style={{ ...baseStyle, textDecoration: 'none', display: 'block' }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--gold)55'; e.currentTarget.style.background = 'var(--panel)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--panel-deep)'; }}
                    >
                      {RowInner}
                    </a>
                  );
                }

                return (
                  <div key={i} style={baseStyle}>
                    {RowInner}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* FOOTER */}
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontFamily: "'Orbitron', monospace", fontSize: '10px', color: 'var(--cyan)', letterSpacing: '2px' }}>© 2026 COLLECTIONKEEPER.ORG</div>
          <div style={{ fontSize: '10px', color: 'var(--border-2)', letterSpacing: '1px' }}>{total} SETS · {confirmedCount} CONFIRMED · 2015-2026</div>
        </div>
      </div>

      <SuggestEditModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        user={user}
        context="Release Timeline"
      />
    </div>
  );
}
